// ADR-0016 §5: the entitlement map from `/v1/me` decides which modes and routes exist.
// `core` is always on. `active` and `trial` are on; `grace` is on but read-only; `disabled`
// or absent is off (hidden navigation, "not enabled" page on a direct URL).
import { createContext, useContext } from "react";
import type { EntitlementStatus, FeatureKey, MeResponse } from "../api/types";
import { featureClosure } from "./addons/catalog";
import type { CompanyAccess } from "./company";

export type FeatureState = {
  feature: FeatureKey;
  status: EntitlementStatus;
  enabled: boolean;
  readOnly: boolean;
};

/** Weakest first: the status a feature gets from itself and its dependencies (`requireFeature`). */
const STRENGTH: Record<EntitlementStatus, number> = { disabled: 0, grace: 1, trial: 2, active: 3 };

export function featureState(me: MeResponse, companyId: string, feature: FeatureKey): FeatureState {
  if (feature === "core") return { feature, status: "active", enabled: true, readOnly: false };
  // The feature and every add-on it depends on: the weakest status wins, as on the server, so
  // an add-on whose dependency is off is off here too (coverage audit row 102).
  const statuses = featureClosure(feature).map(
    (k): EntitlementStatus => me.entitlements[companyId]?.[k] ?? "disabled",
  );
  const status = statuses.reduce((a, b) => (STRENGTH[b] < STRENGTH[a] ? b : a));
  return {
    feature,
    status,
    enabled: status === "active" || status === "trial" || status === "grace",
    readOnly: status === "grace",
  };
}

export type SessionValue = { me: MeResponse; company: CompanyAccess | null };

/** Set by the authenticated layout (me) and the company layout (company). */
export const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside the authenticated shell");
  return value;
}

export function useCompany(): CompanyAccess {
  const { company } = useSession();
  if (!company) throw new Error("useCompany must be used inside /c/$slug");
  return company;
}

/** Entitlement state of `feature` for the current company (or `companyId`). */
export function useFeature(feature: FeatureKey, companyId?: string): FeatureState {
  const { me, company } = useSession();
  const id = companyId ?? company?.id;
  if (!id) throw new Error("useFeature needs a company");
  return featureState(me, id, feature);
}
